/**
 * <quick-add-tile>
 *
 * Self-contained Web Component that adds a "Quick Add" trigger to a product tile
 * and opens an accessible modal where the customer can pick colour, fit, width,
 * and size before adding the product to the bag without leaving the listing.
 *
 * Used by both the Liquid product card (`shared-product-card.liquid`) and the
 * Preact product card (Bloomreach search/PLP). The element only wraps the trigger
 * button; the modal is a shared `modal-slideout` rendered via
 * `snippets/quick-add-tile-templates.liquid`.
 *
 * Markup reuses existing theme components: product-selector-label, color-selector,
 * grid-selector, c-product-price, size-recommendation-bar, and shared-button.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  const MODAL_ID = 'quick-add-tile-modal';
  const FIT_TEMPLATE_ID = 'quick-add-size-recommendation-bar-template';
  const DETAILS_LINK_TEMPLATE_ID = 'quick-add-details-link-template';
  const ADDED_TO_CART_TEMPLATE_ID = 'quick-add-added-to-cart-template';
  const STRINGS_TEMPLATE_ID = 'quick-add-tile-strings';
  const DESKTOP_MQ = '(min-width: 60.25rem)';
  const ADD_TO_CART_SUCCESS_DELAY_MS = 500;

  /**
   * Cached MediaQueryList — evaluated once and kept alive to track resize via
   * the `.matches` property without repeated `matchMedia` calls.
   * @type {MediaQueryList}
   */
  const desktopMQ = window.matchMedia(DESKTOP_MQ);

  // ---------------------------------------------------------------------------
  // JSDoc typedefs
  // ---------------------------------------------------------------------------

  /**
   * @typedef {{ id: string, label: string, swatch?: string, heroImage?: string, image?: string, available?: boolean }} ColourOption
   * @typedef {{ id: string, label: string, available?: boolean }} WidthOption
   * @typedef {{ id: string, label: string, available?: boolean }} SizeOption
   * @typedef {{ node: string, message?: string }} FitData
   * @typedef {{ colour: string|null, width: string|null, size: string|null }} Selection
   * @typedef {{ [key: string]: string }} Strings
   *
   * @typedef {{
   *   productId: string|null,
   *   activeProductGid: string|null,
   *   title: string,
   *   url: string,
   *   image: string,
   *   imageAlt?: string,
   *   price: string,
   *   compareAtPrice: string|null,
   *   hasWidths: boolean,
   *   colours: ColourOption[],
   *   widths: WidthOption[],
   *   sizes: SizeOption[],
   *   fit: FitData|null,
   *   variants: object[],
   *   optionMeta: object|null,
   *   product?: object,
   *   summaryParts?: { labelsHtml: string, priceHtml: string }|null,
   *   gender?: string,
   *   kidsAge?: string,
   *   handle?: string,
   *   isGiftCard?: boolean,
   * }} QuickAddData
   *
   * @typedef {{
   *   fetchQuickAddProduct: (gid: string) => Promise<object>,
   *   buildQuickAddOptions: (product: object, config: object) => { widths: WidthOption[], sizes: SizeOption[], variants: object[], optionMeta: object|null, fit: FitData|null },
   *   productSummaryFromNode: (product: object, strings: Strings) => object,
   *   enrichColours?: (colours: ColourOption[], cache: Map<string,object>) => Promise<{ colours: ColourOption[], productsById: Map<string,object> }>,
   *   mergeColourEnrichment: (cache: Map<string,object>, productsById: Map<string,object>) => void,
   *   normalizeProductGid: (id: string) => string,
   *   resolveQuickAddProduct: (gid: string, cache: Map<string,object>) => Promise<object>,
   *   isCompleteQuickAddProduct?: (product: object) => boolean,
   *   resolveVariantId: (variants: object[], selection: Selection, meta: object|null) => string|null,
   *   updateSizeAvailability?: (sizes: SizeOption[], variants: object[], meta: object|null, width: string) => SizeOption[],
   *   buildLowStockMarkup?: (product: object, selection: Selection, meta: object|null) => string,
   *   buildSizeGenderLabel?: (gender: string, kidsAge: string, labels: object) => string,
   *   buildQuickAddResponsiveImage?: (url: string) => { src: string, srcset?: string, sizes?: string }|null,
   *   applyQuickAddResponsiveImage?: (img: HTMLImageElement, url: string) => void,
   *   filterVisibleColours?: (colours: ColourOption[], activeGid: string) => ColourOption[],
   *   generateGid?: (id: string) => string,
   *   normalizeProductGid?: (id: string) => string,
   * }} QuickAddTileData
   */

  // ---------------------------------------------------------------------------
  // Module-level state
  // ---------------------------------------------------------------------------

  let instanceId = 0;

  /** @type {QuickAddController|null} */
  let activeController = null;

  let cleanupObserverAttached = false;

  /** @type {Strings|null} Memoized strings — populated once from the DOM. */
  let _cachedStrings = null;

  // ---------------------------------------------------------------------------
  // Utility helpers
  // ---------------------------------------------------------------------------

  /** @returns {HTMLElement|null} */
  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  /**
   * Returns the external data-provider object, or null if unavailable.
   * Centralises all access to window.quickAddTileData so callers don't scatter
   * optional-chaining checks across the module.
   * @returns {QuickAddTileData|null}
   */
  function getExternalData() {
    return /** @type {any} */ (window).quickAddTileData ?? null;
  }

  /**
   * Reads and decodes i18n strings from the shared template node.
   * Result is memoized — the DOM node is static after page load.
   * @returns {Strings}
   */
  function getStrings() {
    if (_cachedStrings) return _cachedStrings;

    const node = document.getElementById(STRINGS_TEMPLATE_ID);
    if (!node) {
      _cachedStrings = {};
      return _cachedStrings;
    }

    /** @type {Strings} */
    const strings = {};
    for (const key of Object.keys(node.dataset)) {
      strings[key] = decodeHtmlEntities(node.dataset[key] ?? '');
    }

    _cachedStrings = strings;
    return _cachedStrings;
  }

  /**
   * @param {string} message
   * @returns {string}
   */
  function alertMessageText(message) {
    return decodeHtmlEntities(message);
  }

  /**
   * Escapes a value for safe insertion into an HTML attribute or text node.
   * @param {unknown} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Decodes HTML entities using a throwaway textarea node.
   * @param {unknown} value
   * @returns {string}
   */
  function decodeHtmlEntities(value) {
    if (value == null || value === '') return '';
    const node = document.createElement('textarea');
    node.innerHTML = String(value);
    return node.value;
  }

  /**
   * Returns string config keys used to match option names from product data.
   * @returns {{ sizeOptionMatch: string, widthOptionMatch: string, widthRegular: string }}
   */
  function getStringConfig() {
    const strings = getStrings();
    return {
      sizeOptionMatch: strings.sizeOptionMatch || 'size',
      widthOptionMatch: strings.widthOptionMatch || 'width',
      widthRegular: strings.widthRegular || 'regular',
    };
  }

  /**
   * Returns size gender label markup for the size selector label.
   * @param {string|undefined} gender
   * @param {string|undefined} kidsAge
   * @param {Strings} strings
   * @returns {string}
   */
  function getSizeGenderLabels(strings) {
    return {
      mens: decodeHtmlEntities(strings.sizeGenderMens),
      womens: decodeHtmlEntities(strings.sizeGenderWomens),
      kids: {
        big: decodeHtmlEntities(strings.sizeGenderKidsBig),
        little: decodeHtmlEntities(strings.sizeGenderKidsLittle),
        infant: decodeHtmlEntities(strings.sizeGenderKidsInfant),
      },
    };
  }

  /**
   * @param {string|undefined} gender
   * @param {string|undefined} kidsAge
   * @param {Strings} strings
   * @returns {string}
   */
  function sizeGenderLabelMarkup(gender, kidsAge, strings) {
    const ext = getExternalData();
    const label = ext?.buildSizeGenderLabel
      ? ext.buildSizeGenderLabel(gender, kidsAge, getSizeGenderLabels(strings))
      : '';
    if (!label) return '';
    return `<span class="product-selector-label-current-selection--gender-label">${label}</span>`;
  }

  /**
   * Repeats a markup factory function `count` times and joins the results.
   * @param {number} count
   * @param {(index: number) => string} factory
   * @returns {string}
   */
  function repeatMarkup(count, factory) {
    return Array.from({ length: count }, (_, i) => factory(i)).join('');
  }

  /**
   * Bubble loader markup matching `snippets/shared-bubble-loader.liquid`.
   * @returns {string}
   */
  function buildBubbleLoaderMarkup() {
    return `<div class="bubble-loader_wrapper" aria-hidden="true">${repeatMarkup(
      4,
      (index) => `<span class="bubble-loader_dot" key="${index + 1}"></span>`,
    )}</div>`;
  }

  // ---------------------------------------------------------------------------
  // Data parsing & normalisation
  // ---------------------------------------------------------------------------

  /**
   * Normalises a product GID using available utilities, falling back to a
   * trimmed string representation.
   * @param {string|null|undefined} id
   * @returns {string|null}
   */
  function normalizeQuickAddId(id) {
    if (!id) return null;
    const ext = getExternalData();
    return (
      ext?.normalizeProductGid?.(id) || /** @type {any} */ (window).utilities?.generateGid?.(id) || String(id).trim()
    );
  }

  /**
   * Normalises the `id` field of each colour in the list.
   * @param {ColourOption[]} colours
   * @returns {ColourOption[]}
   */
  function normalizeColourList(colours) {
    if (!Array.isArray(colours)) return colours;
    return colours.map((colour) => ({
      ...colour,
      id: normalizeQuickAddId(colour.id) || colour.id,
    }));
  }

  /**
   * Parses all data for a tile element from its inline JSON script tag and
   * data attributes.
   * @param {HTMLElement} el
   * @returns {QuickAddData}
   */
  function parseData(el) {
    const inline = el.querySelector('script.quick-add-tile__data');
    let payload = {};
    if (inline) {
      try {
        payload = JSON.parse(inline.textContent || '{}');
      } catch (err) {
        console.warn('[quick-add-tile] invalid JSON payload', err);
      }
    }

    const productId = normalizeQuickAddId(el.dataset.productId || payload.productId);
    const colours = normalizeColourList(Array.isArray(payload.colours) ? payload.colours : []);

    return {
      productId,
      activeProductGid: productId,
      title: payload.title || el.dataset.productTitle || '',
      url: payload.url || el.dataset.productUrl || '#',
      image: payload.image || el.dataset.productImage || '',
      price: window.utilities.formatPrice(
        Shopify.currency.active,
        window.utilities.parsePrice(payload.price || el.dataset.productPrice || ''),
      ),
      compareAtPrice:
        window.utilities.formatPrice(
          Shopify.currency.active,
          window.utilities.parsePrice(payload.compareAtPrice || el.dataset.productComparePrice || ''),
        ) || null,
      hasWidths: el.dataset.hasWidths !== 'false',
      colours,
      widths: Array.isArray(payload.widths) ? payload.widths : [],
      sizes: Array.isArray(payload.sizes) ? payload.sizes : [],
      fit: payload.fit === false ? null : payload.fit || null,
      variants: [],
      optionMeta: null,
      isGiftCard: el.dataset.isGiftCard === 'true' || Boolean(payload.isGiftCard),
    };
  }

  // ---------------------------------------------------------------------------
  // Option helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the first option that is not explicitly unavailable.
   * @param {Array<{ available?: boolean }>} options
   * @returns {{ available?: boolean }|null}
   */
  function firstAvailableOption(options) {
    return (options || []).find((opt) => opt.available !== false) || null;
  }

  /**
   * Derives a stable DOM-safe key for a colour radio input from its GID.
   * @param {ColourOption} colour
   * @returns {string}
   */
  function colourInputKey(colour) {
    const gid = String(colour?.id || '');
    const utilities = /** @type {any} */ (window).utilities;
    if (utilities?.extractIdFromGid) {
      return utilities.extractIdFromGid(gid) || gid.replace(/\W/g, '');
    }
    return gid.replace(/\W/g, '');
  }

  /**
   * Returns the colours that should be visible in the modal for a given data
   * snapshot, filtered via the external hook when available.
   * @param {QuickAddData} data
   * @returns {ColourOption[]}
   */
  function visibleColours(data) {
    const colours = data?.colours || [];
    const ext = getExternalData();
    if (!ext?.filterVisibleColours) return colours;
    return ext.filterVisibleColours(colours, data.activeProductGid || data.productId);
  }

  /**
   * Returns true when the modal should render a colour selector.
   * Colours are only supplied by companion products in the tile payload.
   * @param {Partial<QuickAddData>|null|undefined} data
   * @returns {boolean}
   */
  function hasColourOptions(data) {
    return visibleColours(/** @type {QuickAddData} */ (data)).length > 0;
  }

  /**
   * Returns a CSS attribute selector that targets a colour option input by its
   * data-option-value, safely escaping the value.
   * @param {string} colourId
   * @returns {string}
   */
  function colourOptionSelector(colourId) {
    return `[data-option-group="colour"][data-option-value="${String(colourId).replace(/"/g, '\\"')}"]`;
  }

  /**
   * Returns true when two summary snapshots share the same title.
   * @param {{ title?: string }|null} previous
   * @param {{ title?: string }|null} next
   * @returns {boolean}
   */
  function summaryTitleUnchanged(previous, next) {
    return Boolean(previous && next && previous.title === next.title);
  }

  /**
   * Returns true when two summary snapshots share the same price information.
   * @param {{ price?: string, compareAtPrice?: string|null }|null} previous
   * @param {{ price?: string, compareAtPrice?: string|null }|null} next
   * @returns {boolean}
   */
  function summaryPriceUnchanged(previous, next) {
    return Boolean(
      previous && next && previous.price === next.price && previous.compareAtPrice === next.compareAtPrice,
    );
  }

  // ---------------------------------------------------------------------------
  // Skeleton markup
  // ---------------------------------------------------------------------------

  /**
   * Builds the full skeleton loading markup for the modal body.
   * @param {Partial<QuickAddData>} data
   * @returns {string}
   */
  function buildSkeletonMarkup(data) {
    const colours = visibleColours(/** @type {QuickAddData} */ (data));
    const showColours = colours.length > 0;
    const colourCount = showColours ? Math.min(colours.length, 6) : 0;
    const showWidths = data?.hasWidths !== false;
    const promoSkeleton =
      '<div class="quick-add-tile-modal__skeleton-promo"><div class="skeleton-shimmer"></div></div>';

    const colourSkeleton = showColours
      ? `
        <div class="quick-add-tile-modal__skeleton-option-group product-form__input product-color-selector">
          <div class="quick-add-tile-modal__skeleton-label" aria-hidden="true"><div class="skeleton-shimmer"></div></div>
          <ul class="quick-add-tile-modal__skeleton-swatch-list" aria-hidden="true">
            ${repeatMarkup(
              colourCount,
              () =>
                '<li class="quick-add-tile-modal__skeleton-swatch"><div class="color-selector__img-wrapper"><div class="skeleton-shimmer"></div></div></li>',
            )}
          </ul>
        </div>`
      : '';

    const widthSkeleton = showWidths
      ? `
        <div class="quick-add-tile-modal__skeleton-option-group quick-add-tile-modal__width-group product-form__input">
          <div class="quick-add-tile-modal__skeleton-label" aria-hidden="true"><div class="skeleton-shimmer"></div></div>
          <div class="quick-add-tile-modal__skeleton-grid quick-add-tile-modal__skeleton-grid--width" aria-hidden="true">
            ${repeatMarkup(3, () => '<div class="quick-add-tile-modal__skeleton-grid-pill"><div class="skeleton-shimmer"></div></div>')}
          </div>
        </div>`
      : '';

    return `
      <div class="quick-add-tile-modal__body quick-add-tile-modal__body--skeleton" aria-busy="true">
        <div class="quick-add-tile-modal__media" aria-hidden="true">
          <div class="quick-add-tile-modal__skeleton-media"><div class="skeleton-shimmer"></div></div>
        </div>
        <div class="quick-add-tile-modal__content">
          <div class="quick-add-tile-modal__scroll" data-qa-scroll>
            <h2 id="quick-add-tile-title" class="visually-hidden">${escapeHtml(data?.title || 'Product')}</h2>
            <div class="quick-add-tile-modal__summary product__info-wrapper" aria-hidden="true">
              ${promoSkeleton}
              <div class="quick-add-tile-modal__skeleton-title"><div class="skeleton-shimmer"></div></div>
              <div class="quick-add-tile-modal__skeleton-price"><div class="skeleton-shimmer"></div></div>
            </div>
            <div class="quick-add-tile-modal__options quick-add-tile-modal__options--skeleton" aria-hidden="true">
              ${colourSkeleton}
              ${widthSkeleton}
              <div class="quick-add-tile-modal__skeleton-option-group quick-add-tile-modal__size-group product-form__input">
                <div class="quick-add-tile-modal__skeleton-label"><div class="skeleton-shimmer"></div></div>
                <div class="quick-add-tile-modal__skeleton-grid">
                  ${repeatMarkup(12, () => '<div class="quick-add-tile-modal__skeleton-grid-pill"><div class="skeleton-shimmer"></div></div>')}
                </div>
              </div>
            </div>
          </div>
          <div class="quick-add-tile-modal__footer product-form__buttons-wrapper" aria-hidden="true">
            <div class="quick-add-tile-modal__skeleton-button"><div class="skeleton-shimmer"></div></div>
          </div>
        </div>
      </div>`;
  }

  // ---------------------------------------------------------------------------
  // Modal cleanup observer
  // ---------------------------------------------------------------------------

  /**
   * Attaches a single MutationObserver to the shared modal that clears the
   * active controller when the modal's `open` attribute is removed.
   * Safe to call multiple times — attaches only once.
   */
  function attachModalCleanup() {
    if (cleanupObserverAttached) return;

    const modal = getModal();
    if (!modal) return;

    cleanupObserverAttached = true;

    const observer = new MutationObserver(() => {
      if (!modal.hasAttribute('open') && activeController) {
        activeController._clear();
        activeController = null;
      }
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  // ---------------------------------------------------------------------------
  // QuickAddController
  // ---------------------------------------------------------------------------

  class QuickAddController {
    constructor() {
      /** @type {HTMLElement|null} */
      this._root = null;

      /** @type {Selection} */
      this.selection = { colour: null, width: null, size: null };

      /** @type {QuickAddData|null} */
      this.data = null;

      /** @type {Strings} */
      this.strings = getStrings();

      /** @type {string} Unique prefix used for radio group `name` attributes. */
      this._id = `qa-${++instanceId}`;

      /** @type {boolean} */
      this._loading = false;

      /** @type {HTMLElement|null} */
      this._opener = null;

      /** @type {Map<string, object>} Per-controller product cache keyed by GID. */
      this._productCache = new Map();

      /** @type {boolean} Prevents add-button label update during size validation. */
      this._addButtonFrozen = false;

      /** @type {boolean} Set to true once the customer explicitly picks a size. */
      this.sizeSelected = false;

      /**
       * Token incremented on every media swap to cancel stale transitions.
       * @type {number}
       */
      this._mediaSwapToken = 0;

      /**
       * AbortController for the in-flight colour product fetch.
       * Replaced on each colour change so only the latest request applies.
       * @type {AbortController|null}
       */
      this._colourAbortController = null;

      /**
       * Signature of the currently rendered colour list, used to skip
       * unnecessary re-renders.
       * @type {string}
       */
      this._colourOptionsSignature = '';

      /**
       * Signature of the currently rendered variable options (widths + sizes),
       * used to skip unnecessary re-renders.
       * @type {string}
       */
      this._variableSignature = '';

      /**
       * rAF handle for the debounced width-change DOM flush.
       * @type {number|null}
       */
      this._widthRafHandle = null;
    }

    // -------------------------------------------------------------------------
    // Static factory
    // -------------------------------------------------------------------------

    /**
     * Opens the Quick Add modal for a given tile trigger and data snapshot.
     * Clears any previously active controller before opening.
     * @param {HTMLElement} opener
     * @param {QuickAddData} data
     * @returns {Promise<void>}
     */
    static async open(opener, data) {
      attachModalCleanup();

      const modal = getModal();
      if (!modal) {
        console.warn('[quick-add-tile] missing modal-slideout#quick-add-tile-modal');
        return;
      }

      // Explicitly clear any stale controller before creating the new one to
      // avoid the race where the MutationObserver hasn't fired yet.
      if (activeController) {
        activeController._clear();
        activeController = null;
      }

      const controller = new QuickAddController();
      activeController = controller;
      controller._opener = opener;

      const ext = getExternalData();

      if (data.productId && ext?.fetchQuickAddProduct) {
        controller.showLoading(opener, data, modal);
        modal.open(opener);

        try {
          const productGid = data.activeProductGid || data.productId;
          const product = await ext.fetchQuickAddProduct(productGid);
          controller._productCache.set(product.id, product);

          const stringConfig = getStringConfig();
          const options = ext.buildQuickAddOptions(product, stringConfig);
          const summary = ext.productSummaryFromNode(product, controller.strings);
          const prefetched = new Map(controller._productCache);

          let colours = data.colours;
          try {
            const enrichResult =
              data.colours?.length > 0 && ext.enrichColours
                ? await ext.enrichColours(data.colours, prefetched)
                : { colours: data.colours, productsById: prefetched };
            ext.mergeColourEnrichment(controller._productCache, enrichResult.productsById);
            colours = enrichResult.colours;
          } catch (error) {
            console.warn('[quick-add-tile] colour enrichment failed, using tile colours', error);
          }

          controller.show(
            opener,
            {
              ...data,
              ...summary,
              image: summary.image || data.image,
              colours,
              widths: options.widths,
              sizes: options.sizes,
              variants: options.variants,
              optionMeta: options.optionMeta,
              fit: options.fit,
              product,
              summaryParts: summary.summaryParts,
            },
            modal,
          );
        } catch (error) {
          console.error('[quick-add-tile] failed to load product data', error);
          controller.showError(
            opener,
            data,
            modal,
            alertMessageText(controller.strings.errorLoad || 'Unable to load product options'),
          );
        }
        return;
      }

      controller.show(opener, data, modal);
      modal.open(opener);
    }

    // -------------------------------------------------------------------------
    // Loading / error states
    // -------------------------------------------------------------------------

    /**
     * Renders the skeleton loading state into the modal body area.
     * @param {HTMLElement} opener
     * @param {QuickAddData} data
     * @param {HTMLElement} modal
     */
    showLoading(opener, data, modal) {
      this._root = modal;
      this.data = data;
      this._loading = true;
      this.strings = getStrings();
      this._id = `qa-${++instanceId}`;

      const bodyArea = modal.querySelector('[data-qa-body]');
      if (!bodyArea) return;
      this._renderSkeleton(bodyArea);
    }

    /**
     * @param {HTMLElement} bodyArea
     */
    _renderSkeleton(bodyArea) {
      if (!bodyArea) return;
      bodyArea.innerHTML = buildSkeletonMarkup({ ...this.data, strings: this.strings });
    }

    /**
     * Renders a minimal error state into the modal body area.
     * @param {HTMLElement} opener
     * @param {QuickAddData} data
     * @param {HTMLElement} modal
     * @param {string} message
     */
    showError(opener, data, modal, message) {
      this._loading = false;
      this._root = modal;
      this.data = data;
      this.strings = getStrings();

      const bodyArea = modal.querySelector('[data-qa-body]');
      if (!bodyArea) return;

      bodyArea.innerHTML = `
        <div class="quick-add-tile-modal__body">
          <div class="quick-add-tile-modal__content">
            <div class="quick-add-tile-modal__scroll" data-qa-scroll>
              <div class="quick-add-tile-modal__summary product__info-wrapper">
                <div class="product__title">
                  <h2 id="quick-add-tile-title" class="fixed-heading-xs">${escapeHtml(data.title)}</h2>
                </div>
                <p class="quick-add-tile-modal__unavailable fixed-utility-s" role="alert"></p>
              </div>
            </div>
          </div>
        </div>`;

      const errorNode = bodyArea.querySelector('.quick-add-tile-modal__unavailable');
      if (errorNode) {
        errorNode.textContent = alertMessageText(message);
      }
    }

    // -------------------------------------------------------------------------
    // Show / hide / clear
    // -------------------------------------------------------------------------

    /**
     * Fully renders the modal content and wires up all interactions.
     * @param {HTMLElement} opener
     * @param {QuickAddData} data
     * @param {HTMLElement} modal
     */
    show(opener, data, modal) {
      this._root = modal;
      this.data = data;
      this.selection = { colour: null, width: null, size: null };
      this.sizeSelected = false;
      this._addButtonFrozen = false;
      this._mediaSwapToken = 0;
      this.strings = getStrings();
      this._id = `qa-${++instanceId}`;
      this._loading = false;

      const bodyArea = modal.querySelector('[data-qa-body]');
      if (!bodyArea) return;
      this._renderContent(bodyArea);
    }

    /** Closes the modal (and triggers the MutationObserver cleanup). */
    hide() {
      const modal = getModal();
      if (!modal?.hasAttribute('open')) {
        this._clear();
        return;
      }
      modal.close();
    }

    /**
     * Empties the modal body and detaches this controller from the DOM.
     * Called by the MutationObserver when the modal's `open` attribute is removed,
     * or eagerly before a new controller takes over.
     */
    _clear() {
      // Abort any in-flight colour fetch so its result isn't applied after clear.
      this._colourAbortController?.abort();
      this._colourAbortController = null;

      // Cancel any pending rAF for width changes.
      if (this._widthRafHandle !== null) {
        cancelAnimationFrame(this._widthRafHandle);
        this._widthRafHandle = null;
      }

      const modal = this._root || getModal();
      if (!modal) return;

      const bodyArea = modal.querySelector('[data-qa-body]');
      if (bodyArea) bodyArea.innerHTML = '';
      this._root = null;
    }

    // -------------------------------------------------------------------------
    // DOM helpers
    // -------------------------------------------------------------------------

    /**
     * @param {string} selector
     * @returns {HTMLElement|null}
     */
    _query(selector) {
      return this._root?.querySelector(selector) ?? null;
    }

    /**
     * @param {string} selector
     * @returns {HTMLElement[]}
     */
    _queryAll(selector) {
      return this._root ? Array.from(this._root.querySelectorAll(selector)) : [];
    }

    // -------------------------------------------------------------------------
    // Rendering
    // -------------------------------------------------------------------------

    /**
     * Renders the full modal body HTML and wires up all sub-components.
     * @param {HTMLElement} bodyArea
     */
    _renderContent(bodyArea) {
      const d = this.data;
      const colours = visibleColours(d);
      const hasColours = hasColourOptions(d);
      const addLabel = escapeHtml(this.strings.addToBag || 'Add to bag');

      bodyArea.innerHTML = `
        <div class="quick-add-tile-modal__body" data-qa-body-inner>
          <div class="quick-add-tile-modal__media">
            <div class="quick-add-tile-modal__media-stage" data-qa-media-stage>
              ${
                d.image
                  ? this._mediaImageMarkup(d.image, d.imageAlt || d.title, { active: true })
                  : '<div class="quick-add-tile-modal__media-placeholder" aria-hidden="true"></div>'
              }
            </div>
          </div>
          <div class="quick-add-tile-modal__content">
            <div class="quick-add-tile-modal__scroll" data-qa-scroll>
              <div class="quick-add-tile-modal__summary product__info-wrapper" data-qa-summary>
                <div class="product__top-content">
                  <div class="product__descriptors" data-qa-pdp-labels></div>
                  <div class="product__title">
                    <h2 id="quick-add-tile-title" class="fixed-heading-xs">${escapeHtml(d.title)}</h2>
                  </div>
                </div>
                <div data-qa-summary-price></div>
              </div>
              <div class="quick-add-tile-modal__options" data-qa-options>
                ${hasColours ? `<div data-qa-colour-options>${this._colourGroupMarkup(colours)}</div>` : ''}
                ${this._variableOptionsWrapperMarkup(d)}
                <p class="quick-add-tile-modal__unavailable fixed-utility-s" data-qa-unavailable hidden role="alert"></p>
              </div>
              <div data-qa-details-link-mount></div>
            </div>
            <div class="quick-add-tile-modal__footer product-form__buttons-wrapper" data-qa-footer>
              <div class="shared-button-wrapper shared-button-wrapper__style-filled product-form__submit-button--wrapper">
                <button type="button" class="shared-button__button product-form__submit-button" data-qa-add data-added-to-cart="false">
                  <div class="shared-button__state">
                    <span class="shared-button__leading-label fixed-speciality-branded-cta-s-em">${d.isGiftCard ? addLabel : `${addLabel} - ${escapeHtml(window.utilities.formatPrice(d.currency, window.utilities.parsePrice(d.price || '')))}`}</span>
                    ${buildBubbleLoaderMarkup()}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>`;

      this._applySummaryParts(d.summaryParts);
      this._mountDetailsLink(d.url);

      if (d.fit) {
        this._mountFitIndicator(d.fit);
      }

      if (d.isGiftCard) {
        this._mountGiftCardInfoBadge();
        this._mountGiftCardRecipientForm();
        this._bindGiftCardForm();
      }

      this._variableSignature = this._buildVariableSignature(d);
      this._bindOptions();
      this._bindAddButton();
      this._applyDefaultSelections();
      this._colourOptionsSignature = this._colourListSignature(visibleColours(d));
      this._updateAddState();
      this._updateLowStockMessaging();
    }

    /**
     * @param {{ labelsHtml: string, priceHtml: string }|null|undefined} summaryParts
     */
    _applySummaryParts(summaryParts) {
      const labelsMount = this._query('[data-qa-pdp-labels]');
      const priceMount = this._query('[data-qa-summary-price]');
      if (!labelsMount || !priceMount) return;

      labelsMount.innerHTML = summaryParts?.labelsHtml || '';
      priceMount.innerHTML = summaryParts?.priceHtml || '';
    }

    /**
     * @param {string} url
     */
    _mountDetailsLink(url) {
      const mount = this._query('[data-qa-details-link-mount]');
      if (!mount) return;

      mount.innerHTML = '';

      if (!url || url === '#') return;

      const template = document.getElementById(DETAILS_LINK_TEMPLATE_ID);
      const linkRoot = template?.content?.firstElementChild?.cloneNode(true);
      if (!linkRoot) return;

      const link = /** @type {HTMLAnchorElement|null} */ (
        /** @type {HTMLElement} */ (linkRoot).querySelector('[data-qa-details-link]')
      );

      if (link) {
        link.setAttribute('href', url.replace(/^\//, window.Shopify.routes.root));
      }

      mount.appendChild(/** @type {Node} */ (linkRoot));
    }

    _updateDetailsLink() {
      this._mountDetailsLink(this.data?.url);
    }

    /**
     * @param {QuickAddData} [d]
     * @returns {boolean}
     */
    _hasVariableOptions(d = this.data) {
      const hasWidths = Array.isArray(d?.widths) && d.widths.length > 0;
      const hasSizes = Array.isArray(d?.sizes) && d.sizes.length > 0;
      return Boolean(d?.fit) || hasWidths || hasSizes;
    }

    /**
     * @param {QuickAddData} [d]
     * @returns {string}
     */
    _variableOptionsWrapperMarkup(d = this.data) {
      if (!this._hasVariableOptions(d)) return '';

      return `<div
                  class="quick-add-tile-modal__variable-options"
                  data-qa-variable-options
                  aria-label="${escapeHtml(this.strings.optionsRegionLabel || 'Product options')}"
                >
                  ${this._variableOptionsMarkup(d)}
                </div>`;
    }

    /**
     * Builds the inner HTML for the variable options section (fit bar, widths,
     * sizes, low stock).
     * @param {QuickAddData} [d]
     * @returns {string}
     */
    _variableOptionsMarkup(d = this.data) {
      const hasWidths = Array.isArray(d.widths) && d.widths.length > 0;
      const hasSizes = Array.isArray(d.sizes) && d.sizes.length > 0;

      // For gift cards the "sizes" are denomination options — use the option name
      // as the label (e.g. "Denominations") rather than the generic "Size" string.
      const sizeLabel =
        d.isGiftCard && d.optionMeta?.denominationOptionName
          ? d.optionMeta.denominationOptionName
          : this.strings.labelSize || 'Size';

      return `
        ${d.fit ? '<div data-qa-fit></div>' : ''}
        ${hasWidths ? this._gridGroupMarkup('width', this.strings.labelWidth || 'Width', d.widths) : ''}
        ${hasSizes ? this._gridGroupMarkup('size', sizeLabel, d.sizes) : ''}
        <div class="product-low-stock-messaging hidden" data-qa-low-stock hidden></div>`;
    }

    /**
     * Rebuilds the variable options section only when the widths/sizes data has
     * actually changed since the last render.
     */
    _renderVariableOptions() {
      const optionsRoot = this._query('[data-qa-options]');
      if (!optionsRoot) return;

      let mount = this._query('[data-qa-variable-options]');
      const shouldShow = this._hasVariableOptions(this.data);

      if (!shouldShow) {
        mount?.remove();
        this._variableSignature = this._buildVariableSignature(this.data);
        this._updateAddState();
        return;
      }

      if (!mount) {
        mount = document.createElement('div');
        mount.className = 'quick-add-tile-modal__variable-options';
        mount.setAttribute('data-qa-variable-options', '');
        mount.setAttribute('aria-label', this.strings.optionsRegionLabel || 'Product options');

        const unavailable = this._query('[data-qa-unavailable]');
        optionsRoot.insertBefore(mount, unavailable);
        mount.innerHTML = this._variableOptionsMarkup(this.data);
        this._variableSignature = this._buildVariableSignature(this.data);

        if (this.data.fit) {
          this._mountFitIndicator(this.data.fit);
        }

        this._applyDefaultWidthSizeSelections();
        this._updateAddState();
        this._updateLowStockMessaging();
        return;
      }

      const newSignature = this._buildVariableSignature(this.data);
      if (newSignature === this._variableSignature) {
        // Data unchanged — just re-evaluate availability states in place.
        this._updateAddState();
        this._updateLowStockMessaging();
        return;
      }

      // Preserve focus so keyboard/AT users don't lose their position after
      // a colour change re-renders the size/width grid.
      const focused = /** @type {HTMLInputElement|null} */ (document.activeElement);
      const focusGroup = focused?.dataset?.optionGroup;
      const focusValue = focused?.dataset?.optionValue;

      mount.innerHTML = this._variableOptionsMarkup(this.data);
      this._variableSignature = newSignature;

      if (this.data.fit) {
        this._mountFitIndicator(this.data.fit);
      }

      this._applyDefaultWidthSizeSelections();
      this._updateAddState();
      this._updateLowStockMessaging();

      // Restore focus to the equivalent input in the new DOM, if possible.
      if (focusGroup && focusValue) {
        const restored = /** @type {HTMLInputElement|null} */ (
          mount.querySelector(`[data-option-group="${focusGroup}"][data-option-value="${CSS.escape(focusValue)}"]`)
        );
        restored?.focus();
      }
    }

    /**
     * Builds a lightweight signature string representing the current state of
     * widths and sizes. Used to skip unnecessary re-renders.
     * @param {QuickAddData} data
     * @returns {string}
     */
    _buildVariableSignature(data) {
      const w = (data.widths || []).map((o) => `${o.id}:${o.available}`).join('|');
      const s = (data.sizes || []).map((o) => `${o.id}:${o.available}`).join('|');
      return `${w}__${s}`;
    }

    _updateLowStockMessaging() {
      const mount = this._query('[data-qa-low-stock]');
      const product = this.data?.product;
      const ext = getExternalData();
      if (!mount || !product || !ext?.buildLowStockMarkup) return;

      const markup = ext.buildLowStockMarkup(product, this.selection, this.data.optionMeta);

      if (markup) {
        mount.innerHTML = markup;
        mount.classList.remove('hidden');
        mount.removeAttribute('hidden');
      } else {
        mount.innerHTML = '';
        mount.classList.add('hidden');
        mount.setAttribute('hidden', '');
      }
    }

    /**
     * Updates the summary panel (title, price, labels) after a colour change.
     * Only mutates the DOM for fields that have actually changed.
     * @param {{ previousSummary?: object|null }} [options]
     */
    _updateSummaryPanel({ previousSummary = null } = {}) {
      const d = this.data;
      const summary = this._query('[data-qa-summary]');
      if (!summary) return;

      const titleEl = summary.querySelector('#quick-add-tile-title');
      const titleUnchanged = previousSummary && d.title === previousSummary.title;
      if (titleEl && d.title && !titleUnchanged && titleEl.textContent !== d.title) {
        titleEl.textContent = d.title;
      }

      if (!previousSummary || d.handle !== previousSummary.handle || d.summaryParts) {
        this._applySummaryParts(d.summaryParts);
      }

      const priceUnchanged = previousSummary && d.price === previousSummary.price;
      if (!priceUnchanged && d.price) {
        this._syncAddButtonLabel();
      }
    }

    /**
     * Updates the media image and the title/price in the summary area.
     * Only triggers DOM mutations for changed values.
     * @param {{ image?: string, imageAlt?: string, title?: string, price?: string }} summary
     */
    _updateMediaAndSummary(summary = {}) {
      const title = summary.title || this.data?.title || '';
      const imageAlt = summary.imageAlt || this.data?.imageAlt || title;
      const image = summary.image || this.data?.image;

      this._setMediaImage(image, imageAlt);

      const titleEl = this._query('#quick-add-tile-title');
      if (titleEl && summary.title && titleEl.textContent !== summary.title) {
        titleEl.textContent = summary.title;
      }

      const addLabel = this._query('[data-qa-add] .shared-button__leading-label');
      if (addLabel && summary.price) {
        this.data.price = summary.price;
        this._syncAddButtonLabel();
      }
    }

    // -------------------------------------------------------------------------
    // Media image
    // -------------------------------------------------------------------------

    /**
     * Builds the HTML for a product image in the media area.
     * @param {string} imageUrl
     * @param {string} altText
     * @param {{ active?: boolean }} [options]
     * @returns {string}
     */
    _mediaImageMarkup(imageUrl, altText, { active = false } = {}) {
      const ext = getExternalData();
      const attrs = ext?.buildQuickAddResponsiveImage?.(imageUrl);
      if (!attrs) return '';

      const activeClass = active ? ' is-active' : '';
      const srcsetAttr = attrs.srcset ? ` srcset="${escapeHtml(attrs.srcset)}"` : '';
      const sizesAttr = attrs.sizes ? ` sizes="${escapeHtml(attrs.sizes)}"` : '';

      return `<img class="quick-add-tile-modal__media-image${activeClass}" src="${escapeHtml(attrs.src)}"${srcsetAttr}${sizesAttr} alt="${escapeHtml(altText)}" loading="lazy" data-qa-media-image data-image-source="${escapeHtml(imageUrl)}" />`;
    }

    /**
     * Cross-fades to a new product image in the media area.
     * Uses a token to cancel stale transitions when the image changes rapidly.
     * @param {string} image
     * @param {string} [altText]
     */
    _setMediaImage(image, altText = '') {
      const media = this._query('.quick-add-tile-modal__media');
      if (!media) return;

      const normalizedImage = image || '';
      const normalizedAlt = altText || this.data?.imageAlt || this.data?.title || '';
      let stage = media.querySelector('[data-qa-media-stage]');

      if (!stage) {
        stage = document.createElement('div');
        stage.className = 'quick-add-tile-modal__media-stage';
        stage.setAttribute('data-qa-media-stage', '');
        media.innerHTML = '';
        media.appendChild(stage);
      }

      if (!normalizedImage) {
        this._mediaSwapToken++;
        stage.innerHTML = '<div class="quick-add-tile-modal__media-placeholder" aria-hidden="true"></div>';
        return;
      }

      stage.querySelector('.quick-add-tile-modal__media-placeholder')?.remove();

      const images = Array.from(stage.querySelectorAll('.quick-add-tile-modal__media-image'));
      const active = images.find((img) => img.classList.contains('is-active')) || null;

      if (/** @type {HTMLImageElement|null} */ (active)?.dataset.imageSource === normalizedImage) {
        if (normalizedAlt) active.alt = normalizedAlt;
        return;
      }

      const token = ++this._mediaSwapToken;
      let incoming =
        /** @type {HTMLImageElement|null} */ (images.find((img) => !img.classList.contains('is-active'))) || null;

      if (!incoming) {
        incoming = document.createElement('img');
        incoming.className = 'quick-add-tile-modal__media-image';
        incoming.loading = 'lazy';
        incoming.setAttribute('data-qa-media-image', '');
        stage.appendChild(incoming);
      }

      const activateIncoming = () => {
        if (token !== this._mediaSwapToken) return;

        incoming.classList.add('is-active');
        incoming.alt = normalizedAlt;

        if (!active || active === incoming) return;

        active.classList.remove('is-active');

        const retiring = active;
        const cleanup = (/** @type {TransitionEvent} */ event) => {
          if (event.propertyName !== 'opacity') return;
          retiring.removeEventListener('transitionend', cleanup);
          if (!retiring.classList.contains('is-active')) {
            retiring.remove();
          }
        };

        retiring.addEventListener('transitionend', cleanup);
        // Fallback in case transitionend never fires (e.g. reduced-motion).
        setTimeout(() => {
          if (!retiring.isConnected || retiring.classList.contains('is-active')) return;
          retiring.remove();
        }, 300);
      };

      const onReady = () => {
        incoming.removeEventListener('load', onReady);
        incoming.removeEventListener('error', onReady);
        requestAnimationFrame(() => {
          if (token !== this._mediaSwapToken) return;
          activateIncoming();
        });
      };

      incoming.addEventListener('load', onReady);
      incoming.addEventListener('error', onReady);

      const ext = getExternalData();
      ext?.applyQuickAddResponsiveImage?.(incoming, normalizedImage);
      if (incoming.complete) onReady();
    }

    // -------------------------------------------------------------------------
    // Markup builders
    // -------------------------------------------------------------------------

    /**
     * Builds the selector label HTML for a non-size option group.
     * @param {string} group
     * @param {string} label
     * @returns {string}
     */
    _selectorLabelMarkup(group, label) {
      return `
        <label class="product-selector-label">
          <span class="product-selector-label-primary fixed-body-s">${label}</span>
          <span class="product-selector-label-current-selection fixed-body-s-em" data-qa-group-value="${group}"></span>
          <span class="product-selector-label-error fixed-body-s-em" data-qa-error="${group}"></span>
        </label>`;
    }

    /**
     * Builds the selector label HTML for the size option group, including an
     * optional gender label span.
     * @param {string} label
     * @returns {string}
     */
    _sizeSelectorLabelMarkup(label) {
      const genderMarkup = sizeGenderLabelMarkup(this.data?.gender, this.data?.kidsAge, this.strings);

      return `
        <label class="product-selector-label">
          <span class="product-selector-label-primary fixed-body-s">${label}</span>
          <span class="product-selector-label-current-selection fixed-body-s-em" data-qa-group-value="size">
            ${genderMarkup}
            <span class="product-selector-label-current-selection-size" data-qa-size-value></span>
          </span>
          <span class="product-selector-label-error fixed-body-s-em" data-qa-error="size"></span>
        </label>`;
    }

    /**
     * Builds the colour radio group HTML.
     * Uses `<fieldset>` + visually-hidden `<legend>` for correct screen reader
     * grouping — the `role="radiogroup"` previously on the `<ul>` is removed
     * to avoid duplicate group announcements.
     * @param {ColourOption[]} options
     * @returns {string}
     */
    _colourGroupMarkup(options) {
      const label = this.strings.labelColour || 'Colour';
      const name = `${this._id}-colour`;

      const items = options
        .map((opt) => {
          const inputKey = colourInputKey(opt);
          const inputId = `${name}-${inputKey}`;
          const swatchVal = String(opt.swatch || '');
          const isImageSwatch =
            /^https?:\/\//.test(swatchVal) || swatchVal.startsWith('//') || swatchVal.startsWith('/');
          const swatchMarkup = isImageSwatch
            ? `<img class="color-selector__img" src="${escapeHtml(swatchVal)}" alt="${escapeHtml(opt.label)}" loading="lazy" width="32" height="32" />`
            : `<span class="color-selector__img quick-add-tile-modal__swatch-fill" style="background-color:${escapeHtml(swatchVal || 'var(--basic-outline-variant)')}" aria-hidden="true"></span>`;

          return `
            <li class="color-selector-item">
              <div class="color-selector__wrapper">
                <label for="${inputId}" class="color-selector">
                  <input
                    type="radio"
                    id="${inputId}"
                    name="${name}"
                    class="color-selector__radio"
                    value="${inputKey}"
                    data-option-group="colour"
                    data-option-value="${escapeHtml(opt.id)}"
                    aria-label="${escapeHtml(opt.label)}"
                  >
                  <div class="color-selector__img-wrapper">
                    ${swatchMarkup}
                  </div>
                </label>
              </div>
            </li>`;
        })
        .join('');

      return `
        <fieldset class="js product-form__input product-color-selector" data-qa-group="colour">
          <legend class="visually-hidden">${escapeHtml(label)}</legend>
          ${this._selectorLabelMarkup('colour', label)}
          <ul class="color-selector-list" aria-label="${escapeHtml(label)}">
            ${items}
          </ul>
        </fieldset>`;
    }

    /**
     * Builds a grid (pill) radio group HTML for width or size options.
     * Uses `<fieldset>` + visually-hidden `<legend>` for screen reader grouping.
     * @param {'width'|'size'} group
     * @param {string} label
     * @param {(WidthOption|SizeOption)[]} options
     * @returns {string}
     */
    _gridGroupMarkup(group, label, options) {
      const name = `${this._id}-${group}`;
      const groupClass =
        group === 'width'
          ? ' quick-add-tile-modal__width-group'
          : group === 'size'
            ? ' quick-add-tile-modal__size-group'
            : '';
      const defaultOption = firstAvailableOption(options);

      const items = options
        .map((opt) => {
          const disabled = opt.available === false;
          const isDefault = group !== 'size' && defaultOption && String(opt.id) === String(defaultOption.id);
          const inputId = `${name}-${opt.id}`;
          const inputClasses = ['grid-selector__input', disabled ? 'is-unavailable' : ''].filter(Boolean).join(' ');

          return `
            <div class="grid-selector__wrapper">
              <input
                type="radio"
                id="${inputId}"
                name="${name}"
                class="${inputClasses}"
                value="${opt.id}"
                data-option-group="${group}"
                data-option-value="${opt.id}"
                aria-label="${escapeHtml(label)} ${escapeHtml(opt.label)}${disabled ? ', unavailable' : ''}"
                ${disabled ? 'disabled aria-disabled="true"' : ''}
                ${isDefault ? 'checked' : ''}
              >
              <label for="${inputId}" class="grid-selector__label" aria-label="${escapeHtml(opt.label)}">
                <div class="grid-selector__label--inner-container">
                  <div class="grid-selector__label--text-wrapper">
                    <span class="grid-selector__value fixed-utility-m">${escapeHtml(opt.label)}</span>
                  </div>
                </div>
              </label>
            </div>`;
        })
        .join('');

      return `
        <fieldset
          class="js product-form__input${groupClass}"
          data-qa-group="${group}"
          ${group === 'size' ? 'data-size-option-fieldset' : ''}
        >
          <legend class="visually-hidden">${escapeHtml(label)}</legend>
          ${group === 'size' ? this._sizeSelectorLabelMarkup(label) : this._selectorLabelMarkup(group, label)}
          <div class="grid-selectors-container">
            <div class="product-selector-error-border"></div>
            ${items}
          </div>
        </fieldset>`;
    }

    // -------------------------------------------------------------------------
    // Fit indicator
    // -------------------------------------------------------------------------

    /**
     * Clones and mounts the size recommendation bar from its template.
     * @param {FitData} fit
     */
    _mountFitIndicator(fit) {
      const mount = this._query('[data-qa-fit]');
      if (!mount) return;

      if (!fit || !fit.node) {
        mount.remove();
        return;
      }

      const template = document.getElementById(FIT_TEMPLATE_ID);
      if (!template) {
        console.warn('[quick-add-tile] missing size recommendation template');
        mount.remove();
        return;
      }

      const bar = template.content.firstElementChild?.cloneNode(true);
      if (!bar) {
        mount.remove();
        return;
      }

      const node = String(fit.node);
      /** @type {HTMLElement} */ (bar).querySelectorAll('.size-recommendation-bar__node').forEach((el, index) => {
        el.classList.toggle('is-active', String(index + 1) === node);
      });

      const announcement = /** @type {HTMLElement} */ (bar).querySelector(
        '[id^="size-recommendation-bar-announcement-"]',
      );
      if (announcement) {
        announcement.textContent = announcement.textContent.replace(/\d+/, node);
      }

      const message = fit.message || this._fitMessageForNode(node, template);
      const messageEl = /** @type {HTMLElement} */ (bar).querySelector('.size-recommendation-bar__message');
      const messageWrapper = /** @type {HTMLElement} */ (bar).querySelector(
        '.size-recommendation-bar__message-wrapper',
      );
      const trackWrapper = /** @type {HTMLElement} */ (bar).querySelector('.size-recommendation-bar__track-wrapper');

      if (message && messageEl) {
        messageEl.textContent = message;
      } else if (messageWrapper) {
        messageWrapper.remove();
        trackWrapper?.removeAttribute('aria-describedby');
      }

      mount.replaceWith(/** @type {Node} */ (bar));
    }

    /**
     * @param {string} node  '1' | '2' | '3'
     * @param {HTMLTemplateElement} template
     * @returns {string}
     */
    _fitMessageForNode(node, template) {
      if (node === '1') return template.dataset.fitMessageSmall || '';
      if (node === '3') return template.dataset.fitMessageLarge || '';
      return template.dataset.fitMessageTrueToSize || '';
    }

    // -------------------------------------------------------------------------
    // Event binding
    // -------------------------------------------------------------------------

    /** Delegates all option change events from the options container. */
    _bindOptions() {
      const root = this._query('[data-qa-options]');
      if (!root || root.dataset.optionsBound === 'true') return;

      root.dataset.optionsBound = 'true';
      root.addEventListener('change', (event) => {
        const input = /** @type {HTMLInputElement} */ (event.target);
        if (!(input instanceof HTMLInputElement) || !input.dataset.optionGroup || input.disabled) return;
        this._handleOptionChange(input);
      });
    }

    /** Delegates add-to-bag clicks from the footer. */
    _bindAddButton() {
      const footer = this._query('[data-qa-footer]');
      if (!footer || footer.dataset.addBound === 'true') return;

      footer.dataset.addBound = 'true';
      footer.addEventListener('click', (event) => {
        if (/** @type {HTMLElement} */ (event.target).closest('[data-qa-add]')) {
          this._handleAdd();
        }
      });
    }

    // -------------------------------------------------------------------------
    // Option change handling
    // -------------------------------------------------------------------------

    /**
     * Handles a change event on any option radio input.
     * @param {HTMLInputElement} input
     */
    _handleOptionChange(input) {
      const group = input.dataset.optionGroup;
      const value = input.dataset.optionValue;
      this.selection[group] = value;

      if (group === 'colour') {
        // Scope the swatch sync to the colour fieldset rather than the full root.
        const colourGroup = this._query('[data-qa-group="colour"]');
        colourGroup?.querySelectorAll(`[name="${this._id}-colour"]`).forEach((radio) => {
          /** @type {HTMLElement} */ (radio)
            .closest('.color-selector')
            ?.classList.toggle('is-checked', /** @type {HTMLInputElement} */ (radio).checked);
        });
        this._syncColourSwatchStyles();
        this._onColourChange(value);
        return;
      }

      if (group === 'width') {
        this._onWidthChange(value);
      }

      if (group === 'size') {
        this.sizeSelected = true;
        this._clearSizeValidation();
      }

      this._setGroupError(group, null);

      const opt = this._findOption(group, value);
      this._setGroupValueLabel(group, opt ? opt.label : '');

      if (this.data?.isGiftCard && group === 'size' && opt?.label) {
        this._syncGiftCardSummaryPrice(opt.label);
      }

      this._setUnavailable(null);
      this._updateAddState();
      this._updateLowStockMessaging();
    }

    // -------------------------------------------------------------------------
    // Default selections
    // -------------------------------------------------------------------------

    /**
     * Applies default selections for colour, width (desktop only), and
     * optionally size after the modal content is rendered.
     */
    _applyDefaultSelections() {
      const defaultColour = firstAvailableOption(visibleColours(this.data));
      if (defaultColour) {
        const colourInput = this._query(colourOptionSelector(defaultColour.id));
        if (colourInput && !colourInput.disabled) {
          /** @type {HTMLInputElement} */ (colourInput).checked = true;
          colourInput.closest('.color-selector')?.classList.add('is-checked');
          this.selection.colour = defaultColour.id;
          this._setGroupValueLabel('colour', defaultColour.label);
        }
      }

      this._applyDefaultWidthSizeSelections();
    }

    /**
     * Applies the default width selection on desktop viewports and triggers
     * size availability recalculation.
     */
    _applyDefaultWidthSizeSelections() {
      if (desktopMQ.matches && Array.isArray(this.data.widths) && this.data.widths.length > 0) {
        const defaultWidth = firstAvailableOption(this.data.widths);
        if (defaultWidth) {
          const widthInput = /** @type {HTMLInputElement|null} */ (
            this._query(`[name="${this._id}-width"][value="${defaultWidth.id}"]`)
          );
          if (widthInput && !widthInput.disabled) {
            widthInput.checked = true;
            this.selection.width = defaultWidth.id;
            this._setGroupValueLabel('width', defaultWidth.label);
            this._onWidthChange(defaultWidth.id);
          }
        }
      }
    }

    // -------------------------------------------------------------------------
    // Colour options
    // -------------------------------------------------------------------------

    /**
     * @param {string} colourId
     * @returns {string}
     */
    _colourListSignature(colours = []) {
      return colours
        .map((colour) => String(colour?.id || ''))
        .filter(Boolean)
        .join('|');
    }

    /**
     * Rebuilds the colour options DOM only when the visible colour list has
     * actually changed.
     */
    _updateColourOptionsIfNeeded() {
      const colours = visibleColours(this.data);
      const signature = this._colourListSignature(colours);
      if (signature === this._colourOptionsSignature) return;

      this._mountColourOptions(colours);
    }

    /**
     * @param {ColourOption[]} [colours]
     */
    _mountColourOptions(colours = visibleColours(this.data)) {
      const mount = this._query('[data-qa-colour-options]');
      if (!mount) return;

      mount.innerHTML = colours.length > 0 ? this._colourGroupMarkup(colours) : '';
      this._colourOptionsSignature = this._colourListSignature(colours);
    }

    _syncColourSwatchStyles() {
      const scope = this._query('[data-qa-colour-options]') || this._root;
      /** @type {any} */ (window).syncCheckedColorSwatchStyles?.(scope);
    }

    // -------------------------------------------------------------------------
    // Colour change (async, abortable)
    // -------------------------------------------------------------------------

    /**
     * Fetches product data for a new colour selection and applies it to the
     * modal. Any previously in-flight colour fetch is aborted so only the
     * latest selection wins.
     * @param {string} colourId
     * @returns {Promise<void>}
     */
    async _onColourChange(colourId) {
      const ext = getExternalData();
      if (!this.data?.productId || !ext?.resolveQuickAddProduct) return;

      // Abort any previous in-flight fetch.
      this._colourAbortController?.abort();
      const ac = new AbortController();
      this._colourAbortController = ac;

      const normalizedColourId = ext.normalizeProductGid(colourId);
      const colourOption = this._findOption('colour', normalizedColourId) || this._findOption('colour', colourId);
      const cachedProduct = this._productCache.get(normalizedColourId) || this._productCache.get(colourId);
      const isCached = cachedProduct && ext.isCompleteQuickAddProduct?.(cachedProduct);

      const previousSummary = {
        title: this.data.title,
        price: this.data.price,
        compareAtPrice: this.data.compareAtPrice,
        handle: this.data.handle,
      };

      this._setUnavailable(null);
      this.selection.width = null;
      this.selection.size = null;
      this.sizeSelected = false;
      this._clearSizeValidation();

      if (colourOption?.heroImage || colourOption?.image) {
        this._updateMediaAndSummary({ image: colourOption.heroImage || colourOption.image });
      }

      if (!isCached) {
        this._setVariableOptionsLoading(true);
        this._setMediaLoading(true);
      }

      this._updateAddState();

      try {
        const loaded = await this._loadColourProductData(normalizedColourId);

        // Bail if this fetch was superseded by a newer colour selection.
        if (ac.signal.aborted) return;

        this._applyColourProductData(normalizedColourId, {
          ...loaded,
          colourOption,
          previousSummary,
        });
      } catch (error) {
        if (/** @type {DOMException} */ (error).name === 'AbortError') return;
        console.error('[quick-add-tile] failed to load colour product', error);
        this._setUnavailable(alertMessageText(this.strings.errorLoad || 'Unable to load product options'));
      } finally {
        // Only clear loading states if this fetch wasn't aborted by a newer one.
        if (!ac.signal.aborted) {
          this._setVariableOptionsLoading(false);
          this._setMediaLoading(false);
        }
      }
    }

    /**
     * Loads product data for a colour GID, using the product cache when
     * available.
     * @param {string} colourId Normalised GID
     * @returns {Promise<{ product: object, options: object, summary: object, colours: ColourOption[] }>}
     */
    async _loadColourProductData(colourId) {
      const ext = getExternalData();
      const product = await ext.resolveQuickAddProduct(colourId, this._productCache);
      const stringConfig = getStringConfig();
      const options = ext.buildQuickAddOptions(product, stringConfig);
      const summary = ext.productSummaryFromNode(product, this.strings);

      return { product, options, summary, colours: this.data.colours };
    }

    /**
     * Applies freshly loaded product data for a colour selection to the
     * controller state and updates the relevant DOM regions.
     * @param {string} colourId
     * @param {{ product: object, summary: object, options: object, colours: ColourOption[], colourOption: ColourOption|null, previousSummary: object }} param
     */
    _applyColourProductData(colourId, { product, summary, options, colours, colourOption, previousSummary }) {
      const ext = getExternalData();
      const normalizedColourId = ext.normalizeProductGid(colourId);

      this.data = {
        ...this.data,
        ...summary,
        price:
          window.utilities.formatPrice(Shopify.currency.active, window.utilities.parsePrice(summary.price || '')) ||
          this.data.price,
        compareAtPrice:
          window.utilities.formatPrice(
            Shopify.currency.active,
            window.utilities.parsePrice(summary.compareAtPrice || ''),
          ) || null,
        image: summary.image || colourOption?.heroImage || this.data.image,
        colours,
        product,
        summaryParts: summary.summaryParts,
        activeProductGid: normalizedColourId,
        widths: options.widths,
        sizes: options.sizes,
        variants: options.variants,
        optionMeta: options.optionMeta,
        fit: options.fit,
      };
      this.selection.colour = normalizedColourId;

      this._updateMediaAndSummary({
        image: this.data.image,
        title: summaryTitleUnchanged(previousSummary, summary) ? undefined : this.data.title,
        price: summaryPriceUnchanged(previousSummary, summary) ? undefined : this.data.price,
      });
      this._updateSummaryPanel({ previousSummary });
      this._updateDetailsLink();
      this._updateColourOptionsIfNeeded();
      this._renderVariableOptions();
      this._restoreColourSelection(colourId);
      this._syncColourSwatchStyles();
    }

    // -------------------------------------------------------------------------
    // Width change
    // -------------------------------------------------------------------------

    /**
     * Recalculates size availability for a new width selection.
     * DOM mutations are batched into a single rAF to avoid layout thrashing
     * when the user navigates width options quickly via keyboard.
     * @param {string} widthValue
     */
    _onWidthChange(widthValue) {
      const ext = getExternalData();
      if (!ext?.updateSizeAvailability || !this.data?.variants) return;

      this.data.sizes = ext.updateSizeAvailability(
        this.data.sizes,
        this.data.variants,
        this.data.optionMeta,
        widthValue,
      );

      // Cancel any previously scheduled rAF so we only flush once per
      // logical width change, even if called synchronously several times.
      if (this._widthRafHandle !== null) {
        cancelAnimationFrame(this._widthRafHandle);
      }

      this._widthRafHandle = requestAnimationFrame(() => {
        this._widthRafHandle = null;
        this._flushWidthSizeUpdate();
        this._updateAddState();
        this._updateLowStockMessaging();
      });
    }

    /**
     * Applies the current `data.sizes` availability state to the size
     * radio inputs in the DOM without rebuilding the whole group.
     */
    _flushWidthSizeUpdate() {
      const sizeGroup = this._query('[data-qa-group="size"]');
      if (!sizeGroup) return;

      this.data.sizes.forEach((size) => {
        const input = /** @type {HTMLInputElement|null} */ (
          sizeGroup.querySelector(`[data-option-value="${CSS.escape(String(size.id))}"]`)
        );
        if (!input) return;

        const unavailable = size.available === false;
        input.classList.toggle('is-unavailable', unavailable);
        input.disabled = unavailable;
        input.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

        if (unavailable && input.checked) {
          input.checked = false;
          this.selection.size = null;
          this.sizeSelected = false;
          this._setGroupValueLabel('size', '');
        }
      });
    }

    // -------------------------------------------------------------------------
    // Selection state helpers
    // -------------------------------------------------------------------------

    /**
     * @param {string} colourId
     */
    _restoreColourSelection(colourId) {
      const colourInput = /** @type {HTMLInputElement|null} */ (this._query(colourOptionSelector(colourId)));
      if (!colourInput || colourInput.disabled) return;

      colourInput.checked = true;
      colourInput.closest('.color-selector')?.classList.add('is-checked');
      this.selection.colour = colourId;

      const opt = this._findOption('colour', colourId);
      this._setGroupValueLabel('colour', opt ? opt.label : '');
    }

    /**
     * @param {'colour'|'width'|'size'} group
     * @param {string} value
     * @returns {ColourOption|WidthOption|SizeOption|null}
     */
    _findOption(group, value) {
      const sources = {
        colour: visibleColours(this.data),
        width: this.data.widths,
        size: this.data.sizes,
      };
      return (sources[group] || []).find((o) => String(o.id) === String(value)) || null;
    }

    /**
     * Returns the list of option groups that are required for this product.
     * @returns {string[]}
     */
    _requiredGroups() {
      const groups = [];
      if (hasColourOptions(this.data)) groups.push('colour');
      if (Array.isArray(this.data.sizes) && this.data.sizes.length > 0) groups.push('size');
      if (Array.isArray(this.data.widths) && this.data.widths.length > 0 && desktopMQ.matches) groups.push('width');
      return groups;
    }

    /** @returns {string[]} */
    _missingGroups() {
      return this._requiredGroups().filter((g) => !this.selection[g]);
    }

    /** @returns {boolean} */
    _hasSizeGrid() {
      return Array.isArray(this.data?.sizes) && this.data.sizes.length > 0;
    }

    /** @returns {boolean} */
    _isSizeSelectionValid() {
      if (!this._hasSizeGrid()) return true;
      return Boolean(this.sizeSelected && this.selection.size);
    }

    // -------------------------------------------------------------------------
    // Add-button state
    // -------------------------------------------------------------------------

    _syncGiftCardSummaryPrice(priceLabel) {
      const mainPrice = this._query('[data-qa-summary-price] #main-price');
      const priceText = mainPrice?.querySelector('.fixed-heading-s') || mainPrice;
      if (priceText) priceText.textContent = priceLabel;
      this.data.price = priceLabel;
    }

    _syncAddButtonLabel() {
      const addLabel = this._query('[data-qa-add] .shared-button__leading-label');
      if (!addLabel || this._addButtonFrozen) return;

      const label = this.strings.addToBag || 'Add to bag';

      // For gift cards the denomination value IS the label (e.g. "$50"). Show it
      // once the customer has selected one; otherwise just show the base label.
      if (this.data?.isGiftCard) {
        addLabel.textContent = this.selection.size ? `${label} - ${this.selection.size}` : label;
        return;
      }

      const price = this.data?.price;
      addLabel.textContent = price ? `${label} - ${price}` : label;
    }

    /**
     * Shows a size validation error and freezes the add button.
     * The button label is left unchanged so that its accessible name remains
     * meaningful — the error is surfaced exclusively via the error span
     * (`[data-qa-error="size"]`) and by shifting focus to the size grid.
     */
    _showSizeValidationError() {
      const message =
        this.strings.errorSize || /** @type {any} */ (window).variantStrings?.select_size || 'Please select a size';
      this._setGroupError('size', message);
      this._addButtonFrozen = true;

      const firstSize = this._query('[data-qa-group="size"] input:not([disabled])');
      firstSize?.focus();
    }

    _clearSizeValidation() {
      this._setGroupError('size', null);
      if (!this._addButtonFrozen) return;

      this._addButtonFrozen = false;
      this._syncAddButtonLabel();
    }

    _updateAddState() {
      const addBtn = this._query('[data-qa-add]');
      if (!addBtn || addBtn.classList.contains('is-loading') || addBtn.dataset.addedToCart === 'true') return;

      addBtn.disabled = false;
      addBtn.removeAttribute('aria-disabled');
      addBtn.classList.remove('is-disabled');
      this._syncAddButtonLabel();
    }

    /**
     * @param {HTMLButtonElement|null|undefined} addBtn
     */
    _showAddToCartSuccess(addBtn) {
      if (!addBtn) return;

      addBtn.classList.remove('is-loading');
      addBtn.removeAttribute('aria-disabled');
      addBtn.disabled = false;
      addBtn.dataset.addedToCart = 'true';
      addBtn.classList.add('added-to-cart', 'product-form__submit');

      const wrapper = addBtn.closest('.product-form__submit-button--wrapper');
      wrapper?.classList.add('added-to-cart');

      const state = addBtn.querySelector('.shared-button__state');
      const template = document.getElementById(ADDED_TO_CART_TEMPLATE_ID);
      if (state && template) {
        state.innerHTML = template.innerHTML.trim();
      }

      this._addButtonFrozen = true;
    }

    // -------------------------------------------------------------------------
    // Gift card helpers
    // -------------------------------------------------------------------------

    /** Inserts the "Online Exclusive" info badge into the PDP labels area. */
    _mountGiftCardInfoBadge() {
      const labelsMount = this._query('[data-qa-pdp-labels]');
      if (!labelsMount) return;

      const infoText = this.strings.giftCardInfo || '';
      if (!infoText) return;

      const badge = document.createElement('div');
      badge.className = 'product-label';
      badge.innerHTML = `<span class="product-label_secondary-label fixed-utility-m product-label_secondary-label-promotion">${escapeHtml(infoText)}</span>`;
      labelsMount.prepend(badge);
    }

    /**
     * Builds and inserts the recipient form ("Who is this for?") into the scroll
     * area, directly after the options block.
     */
    _mountGiftCardRecipientForm() {
      const scrollArea = this._query('[data-qa-scroll]');
      if (!scrollArea) return;

      const detailsLink = this._query('[data-qa-details-link-mount]');
      const formHtml = this._giftCardRecipientFormMarkup();
      const placeholder = document.createElement('div');
      placeholder.innerHTML = formHtml;
      const formEl = placeholder.firstElementChild;
      if (!formEl) return;

      if (detailsLink) {
        scrollArea.insertBefore(formEl, detailsLink);
      } else {
        scrollArea.appendChild(formEl);
      }
    }

    /**
     * Returns the full HTML for the gift card recipient form.
     * Mirrors `snippets/gift-card-recipient-form.liquid` adapted for JS rendering.
     * Structural classes match the output of the `form-field` snippet so global
     * theme CSS (custom-base.css) handles the floating-label behaviour.
     * @returns {string}
     */
    _giftCardMinSendDate() {
      const today = new Date();
      today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
      return today.toISOString().slice(0, 10);
    }

    _giftCardRecipientFormMarkup() {
      const s = this.strings;
      const id = this._id;
      const heading = s.giftCardHeading || 'Who is this for?';
      const forMe = s.giftCardForMe || 'For me';
      const forFriend = s.giftCardForFriend || 'For a friend';
      const emailLabel = s.giftCardEmailLabel || 'Recipient Email*';
      const nameLabel = s.giftCardNameLabel || 'Recipient Name';
      const sendOnLabel = s.giftCardSendOnLabel || 'Send date (optional)';
      const messageLabel = s.giftCardMessageLabel || 'Message';
      const messagePlaceholder = s.giftCardMessagePlaceholder || 'Write a message';
      const scheduleInfo = s.giftCardScheduleInfo || 'Schedule a day to send the gift card';
      const emailError = s.giftCardEmailError || 'Incorrect email format.';
      const dateError = s.giftCardDateError || 'Incorrect date format.';
      const maxCharsText = s.giftCardMaxChars || 'Maximum 200 characters';
      const minSendOn = this._giftCardMinSendDate();

      // Pull brand-specific icons from the pre-rendered Liquid templates.
      const checkmarkIconHtml = document.getElementById('quick-add-gift-icon-checkmark')?.innerHTML?.trim() || '';
      const calendarIconHtml = document.getElementById('quick-add-gift-icon-calendar')?.innerHTML?.trim() || '';
      const checkmarkSmallIconHtml =
        document.getElementById('quick-add-gift-icon-checkmark-small')?.innerHTML?.trim() || '';
      const addMessageLabel = s.giftCardAddMessage || 'Add a personalized message';

      return `
        <div class="customer quick-add-gift-card-recipient" data-qa-gift-recipient>
          <fieldset class="gift-card-form-option">
            <legend class="recipient-form__send-to">
              <span class="product-selector-label">
                <span class="product-selector-label-primary fixed-body-s">${escapeHtml(heading)}</span>
              </span>
            </legend>
            <label class="gift-card-form-option__button-label">
              <input
                type="radio"
                id="qa-gift-for-me-${id}"
                name="qa-gift-send-to-${id}"
                checked
                value="my-email"
                data-qa-gift-for-me
              >
              <span class="grid-selector__label--inner-container grid-selector__label--text-wrapper grid-selector__value fixed-utility-m">${escapeHtml(forMe)}</span>
            </label>
            <label class="gift-card-form-option__button-label">
              <input
                type="radio"
                id="qa-gift-for-friend-${id}"
                name="qa-gift-send-to-${id}"
                value="recipient-email"
                data-qa-gift-for-friend
              >
              <span class="grid-selector__label--inner-container grid-selector__label--text-wrapper grid-selector__value fixed-utility-m">${escapeHtml(forFriend)}</span>
            </label>
          </fieldset>

          <div class="recipient-fields hidden" data-qa-gift-fields>
            <div class="recipient-fields__field">
              <div class="form-field ${checkmarkIconHtml ? 'form-field__trailing' : ''}">
                <input
                  type="email"
                  id="qa-gift-email-${id}"
                  name="properties[Recipient email]"
                  class="form-field__input fixed-utility-l"
                  value=""
                  autocomplete="email"
                  data-qa-gift-email
                  disabled
                >
                <label for="qa-gift-email-${id}" class="form-field__label">${escapeHtml(emailLabel)}</label>
                ${checkmarkIconHtml ? `<span class="form-field__icon form-field__icon-trailing">${checkmarkIconHtml}</span>` : ''}
                <div class="form-field__messages"></div>
              </div>
              <div class="form__message hidden" id="qa-gift-email-error-${id}" data-qa-gift-email-error>
                <span class="error-message fixed-body-s">${escapeHtml(emailError)}</span>
              </div>
            </div>

            <div class="recipient-fields__field">
              <div class="form-field">
                <input
                  type="text"
                  id="qa-gift-name-${id}"
                  name="properties[Recipient name]"
                  class="form-field__input fixed-utility-l"
                  value=""
                  autocomplete="name"
                  data-qa-gift-name
                  disabled
                >
                <label for="qa-gift-name-${id}" class="form-field__label">${escapeHtml(nameLabel)}</label>
                <div class="form-field__messages"></div>
              </div>
            </div>

            <div class="recipient-fields__field">
              <div class="form-field ${calendarIconHtml ? 'form-field__trailing' : ''} form-field--date-label-top">
                <input
                  type="date"
                  id="qa-gift-sendon-${id}"
                  name="properties[Send on]"
                  class="form-field__input fixed-utility-l"
                  value=""
                  min="${escapeHtml(minSendOn)}"
                  data-qa-gift-sendon
                  disabled
                >
                <label for="qa-gift-sendon-${id}" class="form-field__label">${escapeHtml(sendOnLabel)}</label>
                ${calendarIconHtml ? `<span class="form-field__icon form-field__icon-trailing">${calendarIconHtml}</span>` : ''}
                <div class="form-field__messages"></div>
              </div>
              <div class="form-field__character-count" id="qa-gift-schedule-info-${id}">
                <span class="fixed-utility-s">${escapeHtml(scheduleInfo)}</span>
              </div>
              <div class="form__message hidden" id="qa-gift-sendon-error-${id}" data-qa-gift-sendon-error>
                <span class="error-message fixed-body-s">${escapeHtml(dateError)}</span>
              </div>
            </div>

            <label for="qa-gift-show-message-${id}" class="checkbox__label checkbox__wrapper show-gf-message">
              <input
                id="qa-gift-show-message-${id}"
                type="checkbox"
                name="qa-gift-show-message-${id}"
                value="${escapeHtml(addMessageLabel)}"
                checked
                class="checkbox__input"
                data-qa-gift-show-message
              >
              <div class="checkbox__label--outter-container">
                <div class="checkbox__label--focus-inner">
                  <div class="checkbox__label--inner-container">
                    <div class="checkbox__label--state-layer">
                      ${checkmarkSmallIconHtml}
                    </div>
                  </div>
                </div>
              </div>
              <div class="checkbox__content-container">
                <div class="checkbox__info-container">
                  <span class="checkbox__title fixed-utility-s">${escapeHtml(addMessageLabel)}</span>
                </div>
              </div>
            </label>

            <div class="recipient-fields__field hidden" data-qa-gift-message-field>
              <div class="form-field">
                <label class="fixed-utility-s" for="qa-gift-message-${id}">${escapeHtml(messageLabel)}</label>
                <textarea
                  rows="10"
                  id="qa-gift-message-${id}"
                  name="properties[Message]"
                  class="text-area form-field__input fixed-utility-l"
                  maxlength="200"
                  placeholder="${escapeHtml(messagePlaceholder)}"
                  data-qa-gift-message
                  disabled
                ></textarea>
              </div>
              <div class="form-field__character-count">
                <span class="fixed-utility-s">${escapeHtml(maxCharsText)}</span>
              </div>
            </div>
          </div>

          <input type="hidden" name="properties[__shopify_send_gift_card_to_recipient]" value="if_present" id="qa-gift-control-${id}" data-qa-gift-control disabled>
          <input type="hidden" name="properties[__shopify_offset]" value="" id="qa-gift-offset-${id}" data-qa-gift-offset disabled>
        </div>`;
    }

    /**
     * Wires up the "For me / For a friend" radio toggle and sets the timezone
     * offset value on the hidden field.
     */
    _bindGiftCardForm() {
      const recipientRoot = this._query('[data-qa-gift-recipient]');
      if (!recipientRoot) return;

      const forFriendRadio = /** @type {HTMLInputElement|null} */ (
        recipientRoot.querySelector('[data-qa-gift-for-friend]')
      );
      const forMeRadio = /** @type {HTMLInputElement|null} */ (recipientRoot.querySelector('[data-qa-gift-for-me]'));
      const fields = /** @type {HTMLElement|null} */ (recipientRoot.querySelector('[data-qa-gift-fields]'));
      const controlField = /** @type {HTMLInputElement|null} */ (recipientRoot.querySelector('[data-qa-gift-control]'));
      const offsetField = /** @type {HTMLInputElement|null} */ (recipientRoot.querySelector('[data-qa-gift-offset]'));
      const showMessageCheckbox = /** @type {HTMLInputElement|null} */ (
        recipientRoot.querySelector('[data-qa-gift-show-message]')
      );
      const messageFieldWrapper = /** @type {HTMLElement|null} */ (
        recipientRoot.querySelector('[data-qa-gift-message-field]')
      );
      const messageTextarea = /** @type {HTMLTextAreaElement|null} */ (
        recipientRoot.querySelector('[data-qa-gift-message]')
      );

      if (offsetField) {
        offsetField.value = String(new Date().getTimezoneOffset());
      }

      // Toggles the message field based on the checkbox and the "For a friend" state.
      const toggleMessageField = () => {
        const isFriend = Boolean(forFriendRadio?.checked);
        const showMessage = isFriend && Boolean(showMessageCheckbox?.checked);
        messageFieldWrapper?.classList.toggle('hidden', !showMessage);
        if (messageTextarea) messageTextarea.disabled = !showMessage;
      };

      // Toggles ALL recipient fields (email, name, date) and the hidden control
      // inputs when switching between "For me" / "For a friend".
      const toggleFields = () => {
        const isFriend = Boolean(forFriendRadio?.checked);
        fields?.classList.toggle('hidden', !isFriend);
        if (controlField) controlField.disabled = !isFriend;
        if (offsetField) offsetField.disabled = !isFriend;

        // Enable/disable the non-message inputs (message handled by toggleMessageField).
        Array.from(
          fields?.querySelectorAll('input:not([data-qa-gift-show-message]), textarea:not([data-qa-gift-message])') ||
            [],
        ).forEach((/** @type {HTMLInputElement} */ el) => {
          el.disabled = !isFriend;
        });

        // Re-evaluate message visibility after the "For friend" state changes.
        toggleMessageField();

        // Clear validation errors when toggling back to "For me".
        if (!isFriend) {
          this._query('[data-qa-gift-email-error]')?.classList.add('hidden');
          this._query('[data-qa-gift-sendon-error]')?.classList.add('hidden');
        }
      };

      forFriendRadio?.addEventListener('change', toggleFields);
      forMeRadio?.addEventListener('change', toggleFields);
      showMessageCheckbox?.addEventListener('change', toggleMessageField);

      // Mirrors recipient-form.js updateValidIcon / isInputValidForIcon:
      // toggle .form-field__valid on the wrapper so the checkmark icon only
      // appears once the field value is actually valid.
      const emailInput = /** @type {HTMLInputElement|null} */ (recipientRoot.querySelector('[data-qa-gift-email]'));
      const nameInput = /** @type {HTMLInputElement|null} */ (recipientRoot.querySelector('[data-qa-gift-name]'));
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      /** @param {HTMLInputElement} input @param {boolean} valid */
      const setValidIcon = (input, valid) => {
        input.closest('.form-field')?.classList.toggle('form-field__valid', valid);
      };

      emailInput?.addEventListener('input', () => {
        const v = emailInput.value.trim();
        setValidIcon(emailInput, v !== '' && emailRegex.test(v));
      });

      nameInput?.addEventListener('input', () => {
        setValidIcon(nameInput, nameInput.value.trim() !== '' && nameInput.validity.valid);
      });

      // Initialise: apply both toggles so the initial state is correct.
      toggleFields();
      toggleMessageField();
    }

    /**
     * Reads and validates the recipient form fields.
     * Returns the properties object to include in the cart add payload when
     * "For a friend" is selected, an empty object when "For me" is selected,
     * or `null` if validation fails (in which case the add is blocked).
     * @returns {object|null}
     */
    _collectGiftCardProperties() {
      const forFriendRadio = /** @type {HTMLInputElement|null} */ (this._query('[data-qa-gift-for-friend]'));

      // "For me" selected — no recipient properties needed.
      if (!forFriendRadio?.checked) return {};

      const emailInput = /** @type {HTMLInputElement|null} */ (this._query('[data-qa-gift-email]'));
      const emailValue = emailInput?.value?.trim() || '';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailValue || !emailRegex.test(emailValue)) {
        const emailErrorEl = this._query('[data-qa-gift-email-error]');
        emailErrorEl?.classList.remove('hidden');
        emailInput?.focus();
        return null;
      }

      const nameInput = /** @type {HTMLInputElement|null} */ (this._query('[data-qa-gift-name]'));
      const sendOnInput = /** @type {HTMLInputElement|null} */ (this._query('[data-qa-gift-sendon]'));
      const messageInput = /** @type {HTMLTextAreaElement|null} */ (this._query('[data-qa-gift-message]'));
      const offsetInput = /** @type {HTMLInputElement|null} */ (this._query('[data-qa-gift-offset]'));

      const sendOnValue = sendOnInput?.value?.trim() || '';
      if (sendOnValue) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        const minSendOn = this._giftCardMinSendDate();
        if (!dateRegex.test(sendOnValue) || Number.isNaN(Date.parse(sendOnValue)) || sendOnValue < minSendOn) {
          const dateErrorEl = this._query('[data-qa-gift-sendon-error]');
          dateErrorEl?.classList.remove('hidden');
          sendOnInput?.focus();
          return null;
        }
      }

      /** @type {Record<string, string>} */
      const properties = {
        'Recipient email': emailValue,
        __shopify_send_gift_card_to_recipient: 'if_present',
      };

      const nameValue = nameInput?.value?.trim() || '';
      if (nameValue) properties['Recipient name'] = nameValue;

      if (sendOnValue) properties['Send on'] = sendOnValue;

      const messageValue = messageInput?.value?.trim() || '';
      if (messageValue) properties['Message'] = messageValue;

      if (offsetInput?.value) properties['__shopify_offset'] = offsetInput.value;

      return properties;
    }

    // -------------------------------------------------------------------------
    // UI state helpers
    // -------------------------------------------------------------------------

    /**
     * @param {'colour'|'width'|'size'} group
     * @param {string|null} message
     */
    _setGroupError(group, message) {
      const error = this._query(`[data-qa-error="${group}"]`);
      const fieldset = this._query(`[data-qa-group="${group}"]`);
      if (!error || !fieldset) return;

      if (message) {
        error.textContent = message;
        fieldset.classList.add('is-error');
      } else {
        error.textContent = '';
        fieldset.classList.remove('is-error');
      }
    }

    /**
     * @param {'colour'|'width'|'size'} group
     * @param {string} text
     */
    _setGroupValueLabel(group, text) {
      const label = text || '';

      if (group === 'size') {
        const sizeValueEl = this._query('[data-qa-size-value]');
        if (sizeValueEl) {
          const hasGender = Boolean(this._query('.product-selector-label-current-selection--gender-label'));
          sizeValueEl.textContent = label ? (hasGender ? ` ${label}` : label) : '';
          return;
        }
      }

      const valueLabel = this._query(`[data-qa-group-value="${group}"]`);
      if (valueLabel) {
        valueLabel.textContent = label;
      }
    }

    /**
     * @param {string|null} message
     */
    _setUnavailable(message) {
      const node = this._query('[data-qa-unavailable]');
      if (!node) return;

      if (message) {
        node.textContent = alertMessageText(message);
        node.removeAttribute('hidden');
      } else {
        node.textContent = '';
        node.setAttribute('hidden', '');
      }
    }

    /**
     * @param {boolean} loading
     */
    _setVariableOptionsLoading(loading) {
      const mount = this._query('[data-qa-variable-options]');
      if (!mount) return;

      if (loading) {
        if (mount.offsetHeight > 0) mount.style.minHeight = `${mount.offsetHeight}px`;
        mount.classList.add('is-loading');
        mount.setAttribute('aria-busy', 'true');
        return;
      }

      mount.classList.remove('is-loading');
      mount.removeAttribute('aria-busy');
      mount.style.minHeight = '';
    }

    /**
     * @param {boolean} loading
     */
    _setMediaLoading(loading) {
      this._query('.quick-add-tile-modal__media')?.classList.toggle('is-loading', loading);
    }

    // -------------------------------------------------------------------------
    // Add to bag
    // -------------------------------------------------------------------------

    _handleAdd() {
      const addBtn = this._query('[data-qa-add]');
      if (!addBtn || addBtn.classList.contains('is-loading') || addBtn.dataset.addedToCart === 'true') return;

      const labels = {
        colour: this.strings.errorColour || 'Please select a colour',
        width: this.strings.errorWidth || 'Please select a width',
        size: this.strings.errorSize || 'Please select a size',
      };

      if (this._hasSizeGrid() && !this._isSizeSelectionValid()) {
        this._showSizeValidationError();
        return;
      }

      const missing = this._missingGroups();
      if (missing.length > 0) {
        missing.forEach((group) => this._setGroupError(group, labels[group]));
        const firstError = this._query(`[data-qa-group="${missing[0]}"] input:not([disabled])`);
        if (firstError) firstError.focus();
        return;
      }

      const ext = getExternalData();
      const variantId =
        this.data?.productId && ext?.resolveVariantId
          ? ext.resolveVariantId(this.data.variants, this.selection, this.data.optionMeta)
          : null;

      if (this.data?.productId && !variantId) {
        this._setUnavailable(alertMessageText(this.strings.errorVariant || 'This combination is unavailable'));
        return;
      }

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      if (variantId && typeof routes !== 'undefined' && routes.cart_add_url) {
        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };

        config.headers = {
          ...config.headers,
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };

        const cartItem = { id: variantId, quantity: 1 };

        if (this.data?.isGiftCard) {
          const recipientProperties = this._collectGiftCardProperties();
          if (recipientProperties === null) return; // validation failed
          if (recipientProperties) {
            cartItem.properties = recipientProperties;
          }
        }

        addBtn.setAttribute('aria-disabled', 'true');
        addBtn.classList.add('is-loading');

        const payload = { items: [cartItem] };

        if (cart?.getSectionsToRender) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        config.body = JSON.stringify(payload);

        fetch(routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this._setUnavailable(
                alertMessageText(response.description || response.message || this.strings.errorLoad),
              );
              if (addBtn) {
                addBtn.classList.remove('is-loading');
                this._updateAddState();
              }
              return;
            }

            this._root?.dispatchEvent(
              new CustomEvent('quick-add-tile:add', {
                bubbles: true,
                detail: { product: this.data, selection: { ...this.selection }, variantId },
              }),
            );

            this._showAddToCartSuccess(addBtn);

            window.setTimeout(() => {
              this.hide();

              if (cart) {
                if (typeof cart.renderContents === 'function') cart.renderContents(response);
              } else if (window.routes?.cart_url) {
                window.location = window.routes.cart_url;
                return;
              }

              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'quick-add-tile',
                  productVariantId: variantId,
                  cartData: response,
                });
              }
            }, ADD_TO_CART_SUCCESS_DELAY_MS);
          })
          .catch((error) => {
            console.error('[quick-add-tile] cart add failed', error);
            this._setUnavailable(alertMessageText(this.strings.errorLoad || 'Unable to add to bag'));
            if (addBtn) {
              addBtn.classList.remove('is-loading');
              this._updateAddState();
            }
          });

        return;
      }

      this._root?.dispatchEvent(
        new CustomEvent('quick-add-tile:add', {
          bubbles: true,
          detail: { product: this.data, selection: { ...this.selection } },
        }),
      );

      this.hide();
      if (cart && typeof cart.open === 'function') {
        cart.open();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // QuickAddTile custom element
  // ---------------------------------------------------------------------------

  class QuickAddTile extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.quick-add-tile__trigger');
      if (!this.trigger || this._bound) return;
      this._bound = true;
      this.trigger.addEventListener('click', this._onClick.bind(this));
    }

    /** @param {MouseEvent} event */
    _onClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.trigger?.disabled) return;

      const data = parseData(this);
      if (this.trigger) this.trigger.disabled = true;

      QuickAddController.open(this.trigger, data).finally(() => {
        if (this.trigger) this.trigger.disabled = false;
      });
    }
  }

  if (!customElements.get('quick-add-tile')) {
    customElements.define('quick-add-tile', QuickAddTile);
  }
})();
